
import React, { useState, useEffect, Suspense } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import ToastNotification, { ToastType } from './components/ToastNotification';
import { ArrowUp } from 'lucide-react';
import type { PageType } from './types';

// Lazy Load Pages & Components to optimize initial load
// Heavy components below the fold are now lazy loaded
const Features = React.lazy(() => import('./components/Features'));
const Stats = React.lazy(() => import('./components/Stats'));
const PieCharts = React.lazy(() => import('./components/PieCharts'));

const UtmBuilder = React.lazy(() => import('./components/UtmBuilder'));
const About = React.lazy(() => import('./components/About'));
const LinkChecker = React.lazy(() => import('./components/LinkChecker'));
const LinkStats = React.lazy(() => import('./components/LinkStats'));
const ReportLink = React.lazy(() => import('./components/ReportLink'));
const Terms = React.lazy(() => import('./components/Terms'));
const FAQ = React.lazy(() => import('./components/FAQ'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const QrMaker = React.lazy(() => import('./components/QrMaker'));
const Ads = React.lazy(() => import('./components/Ads'));
const Monetization = React.lazy(() => import('./components/Monetization'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
    <div className="w-10 h-10 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Fallback for sections within the page (smaller loader)
const SectionFallback = () => (
  <div className="py-20 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin"></div>
  </div>
);

const App: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Track where the login was initiated from to decide redirect logic
  const [loginSource, setLoginSource] = useState<'header' | 'hero'>('header');
  
  // Toast State
  const [toast, setToast] = useState<{ message: string; type: ToastType; isVisible: boolean }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const getPageFromHash = (): PageType => {
    // Remove the '#' character
    const hash = window.location.hash.substring(1).toLowerCase();
    
    // Normalize dashboard path
    if (hash === 'dashboard' || hash.startsWith('dashboard/')) return 'dashboard';
    if (hash === 'utm') return 'utm';
    if (hash === 'qr') return 'qr';
    if (hash === 'checker') return 'checker';
    if (hash === 'stats') return 'stats';
    if (hash === 'report') return 'report';
    if (hash === 'terms') return 'terms';
    if (hash === 'ads') return 'ads';
    if (hash === 'monetization') return 'monetization';
    if (hash === 'about') return 'about';
    if (hash === 'faq') return 'faq';
    
    return 'home';
  };

  useEffect(() => {
    // Check local storage only. Default to light mode if not set.
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    }

    const initApp = async () => {
      let userIsLoggedIn = false;
      
      // 1. Check Auth
      try {
        const response = await fetch('https://api.f4st.ir/api/profile', {
          method: 'GET',
          credentials: 'include', // Ensures the access_token cookie is sent
        });
        if (response.ok) {
          userIsLoggedIn = true;
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Failed to check auth status:', error);
        setIsLoggedIn(false);
      }

      // 2. Handle Routing based on Hash
      const page = getPageFromHash();

      if (page === 'dashboard') {
        if (userIsLoggedIn) {
          setCurrentPage('dashboard');
        } else {
          // If trying to access dashboard but not logged in, redirect to home
          window.location.hash = '';
          setCurrentPage('home');
        }
      } else {
        setCurrentPage(page);
      }
      
      setIsAuthChecking(false);
    };
    
    initApp();

    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    // Handle Hash Change (Back/Forward buttons)
    const handleHashChange = () => {
       const page = getPageFromHash();
       setCurrentPage(page);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const toggleTheme = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDarkMode(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDarkMode(true);
    }
  };

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleNavigate = (page: PageType) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Update URL Hash
    const hash = page === 'home' ? '' : `#${page}`;
    if (window.location.hash !== hash) {
       window.location.hash = hash;
    }
  };

  const handleOpenAuth = (source: 'header' | 'hero' = 'header') => {
    setLoginSource(source);
    setIsAuthModalOpen(true);
  };

  const handleLoginSuccess = () => {
    setIsAuthModalOpen(false);
    setIsLoggedIn(true);
    showToast('با موفقیت وارد شدید', 'success');
    
    // Only redirect to dashboard if login was initiated from header
    // If initiated from Hero (shortening link), stay on page
    if (loginSource === 'header') {
      handleNavigate('dashboard');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('https://api.f4st.ir/api/auth/logout', {
        method: 'POST',
        credentials: 'include', // Send cookie so server knows which session to clear
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setIsLoggedIn(false);
    handleNavigate('home');
    showToast('با موفقیت خارج شدید', 'info');
  };

  const renderContent = () => {
    if (isAuthChecking) {
       return <LoadingFallback />;
    }

    // Auth Guard for Dashboard in render
    if (currentPage === 'dashboard' && !isLoggedIn) {
      // Force redirect if somehow state got here without auth
      setTimeout(() => handleNavigate('home'), 0);
      return null;
    }

    return (
      <Suspense fallback={<LoadingFallback />}>
        {(() => {
          switch (currentPage) {
            case 'dashboard':
               return (
                  <Dashboard 
                    onNavigate={handleNavigate} 
                    isDarkMode={isDarkMode} 
                    onLogout={handleLogout}
                    toggleTheme={toggleTheme}
                    showToast={showToast}
                  />
               );
            case 'utm':
              return <UtmBuilder showToast={showToast} />;
            case 'qr':
              return <QrMaker showToast={showToast} />;
            case 'about':
              return <About />;
            case 'checker':
              return <LinkChecker showToast={showToast} />;
            case 'stats':
              return <LinkStats isDarkMode={isDarkMode} showToast={showToast} />;
            case 'report':
              return <ReportLink showToast={showToast} />;
            case 'terms':
              return <Terms />;
            case 'faq':
              return <FAQ onNavigate={handleNavigate} />;
            case 'ads':
              return <Ads showToast={showToast} />;
            case 'monetization':
              return <Monetization />;
            case 'home':
            default:
              return (
                <>
                  <Hero 
                    onNavigate={handleNavigate} 
                    isLoggedIn={isLoggedIn}
                    onOpenAuth={() => handleOpenAuth('hero')}
                    showToast={showToast}
                  />
                  <Suspense fallback={<SectionFallback />}>
                    <Features />
                  </Suspense>
                  <Suspense fallback={<SectionFallback />}>
                    <Stats isDarkMode={isDarkMode} />
                  </Suspense>
                  <Suspense fallback={<SectionFallback />}>
                    <PieCharts />
                  </Suspense>
                </>
              );
          }
        })()}
      </Suspense>
    );
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-slate-50 dark:bg-slate-950 transition-colors duration-300 relative">
      
      {/* Global Stars (Visible in dark mode) */}
      <div className="fixed inset-0 z-0 pointer-events-none hidden dark:block">
         {[...Array(50)].map((_, i) => (
           <div
             key={`star-${i}`}
             className="absolute bg-slate-500 rounded-full opacity-20 animate-pulse"
             style={{
               top: `${Math.random() * 100}%`,
               left: `${Math.random() * 100}%`,
               width: `${Math.random() * 2 + 1}px`,
               height: `${Math.random() * 2 + 1}px`,
               animationDelay: `${Math.random() * 5}s`,
             }}
           />
        ))}
      </div>

      {!isAuthChecking && currentPage !== 'dashboard' && (
        <Header 
          onOpenAuth={() => handleOpenAuth('header')} 
          isLoggedIn={isLoggedIn}
          onNavigate={handleNavigate}
          currentPage={currentPage}
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          onLogout={handleLogout}
        />
      )}
      
      <main className="flex-grow z-10 relative">
         {renderContent()}
      </main>

      {!isAuthChecking && currentPage !== 'dashboard' && <Footer onNavigate={handleNavigate} />}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        showToast={showToast}
      />

      {/* Toast Notification */}
      <ToastNotification 
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-blue-500 text-white shadow-lg flex items-center justify-center transition-all duration-300 z-50 hover:-translate-y-1 hover:shadow-xl ${
          showBackToTop ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'
        }`}
        aria-label="Back to top"
      >
        <ArrowUp size={24} />
      </button>
    </div>
  );
};

export default App;
